/**
 * Google Maps – iframe embed bez API klíče (output=embed).
 * Vhodné pro veřejnou pozvánku; podklad je standardní mapa Google.
 */

export interface MapEmbedInput {
  fullAddress?: string | null;
  ceremonyVenue?: string | null;
  /** weddings.location */
  weddingLocation?: string | null;
  /** Odkaz z nastavení (Google Maps / výjimečně jiný) */
  mapsUrl?: string | null;
}

/** Vrátí src pro <iframe>, nebo null pokud nemáme dost dat. */
export function buildGoogleMapsEmbedSrc(input: MapEmbedInput): string | null {
  const fromUrl = parseQueryFromGoogleMapsUrl(input.mapsUrl);
  if (fromUrl) {
    return toEmbedSrc(fromUrl);
  }
  const fromFields = buildQueryFromFields(input);
  if (fromFields) {
    return toEmbedSrc(fromFields);
  }
  return null;
}

function normPlace(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Sestaví vyhledávací řetězec pro geokódování: spojí název místa a adresu,
 * aby mapa nezůstala jen na obci (typicky fullAddress z DB = jen město).
 */
function buildQueryFromFields(input: MapEmbedInput): string | null {
  const addr = (input.fullAddress || '').trim();
  const venue = (input.ceremonyVenue || '').trim();
  const loc = (input.weddingLocation || '').trim();

  if (venue && addr) {
    const nv = normPlace(venue);
    const na = normPlace(addr);
    if (nv === na) return venue;
    if (na.includes(nv) || nv.includes(na)) {
      return nv.length >= na.length ? venue : addr;
    }
    return `${venue}, ${addr}`;
  }

  if (addr) return addr;

  if (venue && loc && normPlace(venue) !== normPlace(loc)) {
    return `${venue}, ${loc}`;
  }
  if (venue) return venue;
  if (loc) return loc;
  return null;
}

function toEmbedSrc(query: string): string {
  const q = encodeURIComponent(query.trim());
  return `https://www.google.com/maps?q=${q}&output=embed&hl=cs&z=17`;
}

/** Z uživatelského odkazu Google Maps vytáhne vyhledávací řetězec (místo). */
export function parseQueryFromGoogleMapsUrl(mapsUrl: string | null | undefined): string | null {
  if (!mapsUrl || typeof mapsUrl !== 'string') return null;
  const trimmed = mapsUrl.trim();
  if (!trimmed.startsWith('http')) return null;

  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, '');
    const isGoogleMaps =
      host === 'maps.google.com' ||
      host === 'google.com' ||
      host === 'maps.app.goo.gl' ||
      (host.endsWith('.google.com') && u.pathname.includes('/maps'));

    if (!isGoogleMaps) return null;

    // Přesný bod z odkazu „Sdílet“: .../place/.../@49.22,17.74,17z
    const atCoord = u.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atCoord) {
      return `${atCoord[1]},${atCoord[2]}`;
    }

    const q = u.searchParams.get('q');
    if (q) return q.trim();

    const query = u.searchParams.get('query');
    if (query) return query.trim();

    // /maps/place/Name+City/...
    const place = u.pathname.match(/\/maps\/place\/([^/@]+)/);
    if (place) {
      return decodeURIComponent(place[1].replace(/\+/g, ' ')).trim();
    }
  } catch {
    return null;
  }

  return null;
}
